/* eslint-disable eqeqeq */
import { Box, Button, DialogActions, Divider, Grid, Link, Paper, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Encrypt, findCurrency, formatNumber, formatProcessingFee, getCommonConsts, getDisplayFee, isMaskedEmail, maskDigits, showComplianceLink, trimText } from "../../../../../utils";
import AppLoader from "../../../../../components/AppLoader";
import { showMessageBar, getPockytFees, setLastRefId, clearPockytFees } from "../../../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { ACTIVITY_DETAIL, COMPLIANCE_FAILURE, COMPLIANCE_SUCCESS, EVENTS, MESSAGE_BAR_TYPES, paymentOptions, SHOW_PAYMENT_METHOD, DEACTIVE } from "../../../../../utils/constants";
import AppScreenLoader from "../../../../../components/AppScreenLoader";
import * as PropTypes from "prop-types";
import { fiatWithdraw, getComplianceStatus, logEvent } from "../../../../../store/api";
import { useLocation, useNavigate } from "react-router-dom";
import AppToolTip from "../../../../../components/AppTooltip";
import AppPopupWindow from "../../../../../components/AppPopupWindow";
import { colors } from '../../../../../utils/color'
import AppDialogHalfScreen from "../../../../../components/AppDialogHalfScreen";

const DISCLAIMER_RULES = [
  {
    type: paymentOptions.masspay,
    countries: ['ca', 'canada'],
  },
];

const shouldShowDisclaimer = (type, country) => {
  const normalizedType = (type || '').toLowerCase();
  const normalizedCountry = (country || '').toLowerCase();

  return DISCLAIMER_RULES.some((rule) => {
    return rule.type === normalizedType && rule.countries.includes(normalizedCountry);
  });
};

export default function PaymentSummary({
  amount,
  bank,
  paymentCurrency,
  setActive,
  setIsConflict
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const account = useSelector(({ Auth }) => Auth?.account);
  const { t } = useTranslation();
  const location = useLocation();
  const [waitingText, setWaitingText] = useState("");
  const feestructure = useSelector(({ transfer }) => transfer.pocFees);
  const fiatService = useSelector(({ dashboard }) => dashboard?.fiatService);
  const [showTNC, setShowTNC] = useState(false);
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [showFailure, setShowFailure] = useState(false);
  const showTransferDisclaimer = shouldShowDisclaimer(fiatService?.serviceName, bank?.country);
  const fetchComplianceStatus = useCallback(async () => {
    try {
      const response = await getComplianceStatus(bank.country, null, bank.service);
      setComplianceStatus(response.result);
    } catch (err) {
      console.log(err);
    }
  }, [bank]); // Only re-run when `country` changes

  useEffect(() => {
    if (bank && fiatService?.serviceName === paymentOptions.bd && complianceStatus?.status != COMPLIANCE_FAILURE) {
      fetchComplianceStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bank, fiatService?.serviceName])
  useEffect(() => {
    let interval = null;
    if (bank && fiatService?.serviceName === paymentOptions.bd && complianceStatus?.status != COMPLIANCE_FAILURE) {
      interval = setInterval(() => {
        if (complianceStatus?.status != COMPLIANCE_SUCCESS) {
          fetchComplianceStatus();
        } else {
          if (interval) clearInterval(interval)
        }
      }, 20000); // 20 seconds
    }

    return () => {
      if (interval) {
        return clearInterval(interval)
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!bank?.accountToken || !amount) {
      //return to country selec
      setActive(2);
      return
    }
    const encryptedReq = Encrypt(
      JSON.stringify({
        currency: paymentCurrency,
        accountToken: bank.accountToken,
        amount: amount,
        destinationRecordId: bank?.destinationRecordId
      })
    );
    const payload = {
      encryptedReq
    }
    dispatch(getPockytFees(payload, bank.currency, bank.country, setIsConflict));

    return () => {
      dispatch(clearPockytFees());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handlePayment = async () => {
    setLoading(true);
    setWaitingText("Initiating Transfer ...")
    setShowFailure(false);
    try {

      const payload = {
        accountToken: bank.accountToken,
        amount,
        currency: paymentCurrency,
        recordId: feestructure.recordId,
        destinationRecordId: bank?.destinationRecordId
      }
      const data = await fiatWithdraw(payload, bank.currency, bank.country)
      logEvent({
        current: EVENTS.WITHDRAWAL.CONFIRM_FIAT_TRANSFER,
        activity: ACTIVITY_DETAIL.WITHDRAWAL.CONFIRM_FIAT_TRANSFER,
        meta: {
          data: {
            currency: paymentCurrency,
            method: 'xbd',
            accountToken: bank.accountToken,
            pathname: location.pathname,
            source: ACTIVITY_DETAIL.WITHDRAWAL.CONFIRM_FIAT_TRANSFER
          }
        }
      });
      setLoading(false);
      setWaitingText("");
      dispatch(setLastRefId(data.result.id));
      navigate(`/withdraw/status/${data.result.id}`);
    } catch (err) {
      setLoading(false);
      setWaitingText("");
      if (err?.response?.data?.message === DEACTIVE) {
        setShowFailure(true);
        return false;
      } else
        dispatch(showMessageBar(MESSAGE_BAR_TYPES.ERROR, err))
    }
  }

  const getButtonDisabledStatus = () => {
    return loading || feestructure?.error
      || (fiatService?.serviceName === paymentOptions.bd ? complianceStatus?.status !== COMPLIANCE_SUCCESS ? true : false : false)
  }
  if (!Object.keys(feestructure || {}).length > 0) {
    return <AppScreenLoader />;
  }

  function BankFailureModal() {

    return (
      <AppDialogHalfScreen
        show={showFailure}
        onChangeShow={(val) => setShowFailure(val)}
        centralize={true}
      >
        <Box minWidth={300}>
          <Typography
            color="#D4D4D8"
            fontSize={16}
            fontWeight={700}
            marginTop={2}
            fontFamily={"Manrope"}
          >
            {t('unexpectedError')}
          </Typography>
          <Typography
            color="#D4D4D8"
            fontSize={14}
            fontWeight={400}
            marginTop={2}
            fontFamily={"Manrope"}
          >
            {t('ranintoissue')}
          </Typography>
          <Typography
            color="#D4D4D8"
            fontSize={14}
            fontWeight={400}
            marginTop={2}
            fontFamily={"Manrope"}
            data-i18n={`[html]restAssured`}
            textAlign={"left"}>
            <Trans
              components={{
                a: (
                  <Link
                    href="mailto:support@rocketfuel.inc"
                    target="_blank"
                    onClick={() => { }}
                  />
                ),
              }}
            >
              {t('restAssured')}
            </Trans>
          </Typography>
          <Typography
            color="#D4D4D8"
            fontSize={14}
            fontWeight={400}
            marginTop={2}
            fontFamily={"Manrope"}
          >
            {t('thankYou')}
          </Typography>
        </Box>
        <DialogActions
          sx={{
            marginTop: 2,
            display: "flex",
          }}
        >

          <Button
            autoFocus
            onClick={() => setShowFailure(false)}
            variant="contained"
            color="primary"
            sx={{
              color: "#F8FAFF",
              textTransform: "capitalize",
              borderRadius: "8px",
              ":disabled": { backgroundColor: "#0088F5", opacity: 0.4 },
            }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </AppDialogHalfScreen>
    );
  }
  return (
    <Paper
      sx={{
        backgroundColor: "transparent",
        boxShadow: "none",
        paddingTop: 4,
      }}
    >
      <AppPopupWindow 
        src={complianceStatus?.tosLink} 
        title={t('sigin.TnC')} 
        show={showTNC} 
        setShow={setShowTNC}
        width={500}
        height={700}
      />
      {showTransferDisclaimer && (
        <Typography
          color="#D4D4D8"
          fontFamily={"Manrope"}
          fontSize={14}
          fontWeight={400}
          lineHeight={1.2}
          marginBottom={2}
          textAlign="left"
        >
          <Trans
            components={{
              b: <Box component="span" fontWeight={700} />,
            }}
          >
            {t('bank.masspayTransferDisclaimer')}
          </Trans>
        </Typography>
      )}
      <Grid container sx={{ color: "#D6E3F0", margin: 0 }}>
        <Grid item xs={6}>
          <Typography
            fontFamily={"Manrope"}
            fontSize={14}
            textAlign="left"
            fontWeight={400}
            textTransform="capitalize"
            color="#D4D4D8"
          >
            {isMaskedEmail(bank?.accountNumber) ? t("go.bankEmail") :t("go.bank")}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography
            fontFamily={"Manrope"}
            fontSize={14}
            textAlign="right"
            textTransform="capitalize"
            fontWeight={400}
            color="#D4D4D8"
          >
            {maskDigits(bank?.accountNumber)}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography
            fontFamily={"Manrope"}
            fontSize={14}
            fontWeight={400}
            textAlign="left"
            textTransform="capitalize"
            color="#D4D4D8"
          >
            {t("Amount Requested")}:
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography
            fontFamily={"Manrope"}
            fontSize={14}
            fontWeight={400}
            textAlign="right"
            textTransform="capitalize"
            color="#D4D4D8"
          >
            {formatNumber(amount)} {findCurrency(account)}
          </Typography>
        </Grid>
        {(bank?.paymentMethod && SHOW_PAYMENT_METHOD.includes(fiatService?.serviceName)) && <>
          <Grid item xs={4}>
            <Typography fontSize={14} fontWeight={400} color="#D4D4D8">
              {t('selectPaymentBack')}
            </Typography>
          </Grid>
          <Grid item xs={8} textAlign="right">
            <Typography fontSize={14} fontWeight={400} color="#D4D4D8">
              {getCommonConsts(bank?.paymentMethod)}
            </Typography>
          </Grid>
        </>}
        <Grid item xs={6}>
          <Typography
            fontFamily={"Manrope"}
            fontSize={14}
            textAlign="left"
            fontWeight={400}
            textTransform="capitalize"
            color="#D4D4D8"
          >
            {t("Currency requested")}:
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography
            fontFamily={"Manrope"}
            fontSize={14}
            textAlign="right"
            textTransform="uppercase"
            fontWeight={400}
            color="#D4D4D8"
          >
            {paymentCurrency}
          </Typography>
        </Grid>

        {feestructure?.feeEnabled && (
          <>
            <Grid item xs={7}>
              <Typography
                fontFamily={"Manrope"}
                fontSize={14}
                textAlign="left"
                fontWeight={400}
                textTransform="capitalize"
                color="#D4D4D8"
              >
                {t("processing-fee")}:
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography
                fontFamily={"Manrope"}
                fontSize={14}
                textAlign="right"
                textTransform="uppercase"
                fontWeight={400}
                color="#D4D4D8"
              >
               <s style={{ fontSize: 13 }}>{getDisplayFee(feestructure, account)}</s> &nbsp;&nbsp;{formatProcessingFee(feestructure, account)}
              </Typography>
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid
          item
          xs={6}
          fontSize={14}
          textTransform="capitalize"
          color="#D4D4D8"
        >
          <Typography
            fontFamily={"Manrope"}
            fontSize={14}
            textAlign="left"
            textTransform="capitalize"
            fontWeight={400}
            color="#D4D4D8"
          >
            {t('Amount to be sent')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          textAlign="right"
          fontSize={14}
          textTransform="capitalize"
          color="#D4D4D8"
        >
          <Typography
            fontFamily={"Manrope"}
            fontSize={14}
            textAlign="right"
            textTransform="uppercase"
            fontWeight={400}
            color="#D4D4D8"
          >
            {formatNumber(feestructure?.localPayoutAmount)} {findCurrency(account)}
          </Typography>
        </Grid>
        {paymentCurrency.toUpperCase() !== findCurrency(account) &&
          <Grid item xs={6}>
            <Typography
              fontFamily={"Manrope"}
              fontSize={14}
              textAlign="left"
              textTransform="capitalize"
              fontWeight={400}
              color="#D4D4D8"
            >
              {t("exchangeRate")}:
            </Typography>
          </Grid>}
        {paymentCurrency.toUpperCase() !== findCurrency(account) &&
          <Grid item xs={6}>
            <Typography
              fontFamily={"Manrope"}
              fontSize={14}
              textAlign="right"
              textTransform="capitalize"
              fontWeight={400}
              color="#D4D4D8"
            >
              <span>1 {findCurrency(account)} ~ </span>
              <AppToolTip
                enterTouchDelay={0}
                title={`${feestructure?.rkflRate?.toString()} ${feestructure?.currency}`}
                placement="top" arrow>
                <span>{trimText((feestructure?.rkflRate?.toString() || ''), 6)}</span>
              </AppToolTip>&nbsp;{feestructure?.currency}
            </Typography>
          </Grid>}
        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={6}>
          <Typography
            fontFamily={"Manrope"}
            fontSize={14}
            textAlign="left"
            textTransform="capitalize"
            fontWeight={400}
            color="#D4D4D8"
          >
            {t("Amount to be received")}:
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            justifyContent={"flex-end"}
          >
            <Typography
              fontFamily={"Manrope"}
              fontSize={24}
              textAlign="right"
              textTransform="uppercase"
              fontWeight={400}
              color="#D4D4D8"
            >
              {formatNumber(feestructure.payoutAmount || 0)} {(feestructure.currency || '')}
            </Typography>
          </Box>
        </Grid>
        {showComplianceLink(complianceStatus, fiatService) && <Grid item xs={12}>
          {/* <Box display={'flex'} justifyContent={'flex-start'} alignItems={'center'}> */}
            {/* <Box>{complianceStatus?.status === COMPLIANCE_SUCCESS && <Checkbox disabled={complianceStatus?.status === COMPLIANCE_FAILURE}
              checked={complianceStatus?.status === COMPLIANCE_SUCCESS} />}
              {complianceStatus?.status === COMPLIANCE_IN_PROGRESS && <AppLoader />}
            </Box> */}
            {/* <Box><Typography>
              {(complianceStatus?.status !== COMPLIANCE_IN_PROGRESS && complianceStatus?.status !== COMPLIANCE_FAILURE) && <Trans components={{
                a: <Link href="#" onClick={(e) => {
                  e.preventDefault();
                  if (complianceStatus?.tosLink) {
                    setShowTNC(true);
                  }
                }} />
              }}>
                {t('vendor.tnc')}
              </Trans>}

              {complianceStatus?.status === COMPLIANCE_IN_PROGRESS && <Box fontSize={12}>&nbsp;&nbsp;&nbsp;{t('verificationProgress')}</Box>}

            </Typography>
            </Box> */}
          {/* </Box> */}
          {complianceStatus?.status === COMPLIANCE_FAILURE && <Typography sx={{ fontSize: 12, marginTop: 1 }}
            data-i18n={`[html]contactAssistance`}>
            {/* {complianceStatus?.rejectionReason} */}
            <Box color={colors.error}>{t('unknownError')}</Box>
            <Trans
              components={{
                a: (
                  <Link
                    href="mailto:support@rocketfuel.inc"
                    target="_blank"
                    onClick={() => { }}
                  />
                ),
              }}>
              {t('contactAssistance')}
            </Trans>
          </Typography>}
        </Grid>}
        <Grid item xs={12}>
          {waitingText && (
            <Box
              display={"flex"}
              flexDirection="row"
              alignItems={"center"}
              marginTop={1}
              sx={{
                display: "flex",
                flexFlow: "row",
                alignItems: "center",
                marginBottom: 1,
              }}
            >
              <AppLoader
                title={waitingText}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
                fontSize={14}
              />
            </Box>
          )}
        </Grid>
      </Grid>
      <Button
        variant="contained"
        size="large"
        color="primary"
        sx={{
          color: "#041939",
          fontSize: 16,
          width: "100%",
          textTransform: "capitalize",
          borderRadius: "8px",
          ":disabled": { backgroundColor: "#0088F5", opacity: 0.4 },
        }}
        disabled={getButtonDisabledStatus()}
        onClick={handlePayment}
      >
        {t("Confirm")}
      </Button>
      <Box marginTop={1}>
        <Typography
          variant="subtitle1"
          color={"#D4D4D8"}
          textAlign={"left"}
          fontSize={12}
          lineHeight={1.5}
        >
          {t("bankDesclaimer")}
        </Typography>
      </Box>
      <BankFailureModal />
    </Paper>
  );
}

PaymentSummary.propTypes = {
  amount: PropTypes.any,
  // currency: PropTypes.string,
  paymentCurrency: PropTypes.string,
  // paymentMethod: PropTypes.object,
  // feestructure: PropTypes.object,
  bank: PropTypes.object,
  setActive: PropTypes.func
  // loading: PropTypes.bool,
};
